<template>
  <v-card>
    <v-card-title>
      {{ address ? $lang.actions.edit : $lang.actions.add }} {{ $lang.actions.address }}
    </v-card-title>
    <v-card-text>
      <div class="py-1" v-if="!isLoading" style="margin-top: 10px">
        &nbsp;
      </div>
      <v-progress-linear
        class="my-3"
        v-if="isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-form v-model="valid">
        <v-layout column>
          <v-layout row wrap>
            <v-flex
              v-for="(line, index) in filteredLines"
              :key="index"
              xs12 sm6
              :class="{'d-none': ['latitude', 'longitude'].includes(line.property)}"
              class="px-2 prefill">
              <v-text-field
                v-model="formData[line.property]"
                :label="line.label"
                :rules="line.required ? required : []"
                :type="['latitude', 'longitude'].includes(line.property) ? 'number' : 'text'"
                color="primary"
                class="input-wrap"
                :id="String(index)"
                :hint="line.property === 'line1' && formData[line.property] === '' ? $lang.labels.startTyping : ''"
                persistent-hint
                @dblclick="line.property !== 'postalCode' ? filterPrefill('ALL_VALUES', line.property, true) : ''"
                @focus="line.property === 'postalCode' || (country === 'AT' && line.property === 'line3') ? filtering = null : filterPrefill(country === 'UK' && formData[line.property] === '' ? 'ALL_VALUES' : formData[line.property], line.property)"
                @input="['postalCode', 'line1'].includes(line.property) ? getPrefill(formData.postalCode, $event, line.property) : filterPrefill($event, line.property)"
                @blur="['line2'].includes(line.property) && formData[line.property] !== '' ? replaceAddress(formData[line.property], line.property) : ''"
              />
              <ul v-if="!(country === 'AT' && line.property === 'line3') &&
                filtering === line.property &&
                prefill.filtered.length > 0">
                <li
                  v-for="(filtered, index) in filterUniqueFields(prefill.filtered, line.property)"
                  :key="index"
                  @click="replaceAddress(filtered, line.property)">
                  {{ country === 'UK' ? generateAddress(filtered) : filtered }}
                </li>
              </ul>
              <ul v-if="line.property === 'postalCode' &&
                filtering === line.property &&
                postalCodes.length > 0">
                <li
                  v-for="(filtered, index) in postalCodes"
                  :key="index"
                  @click="setPostalCode(filtered)">
                  {{ filtered }}
                </li>
              </ul>
              <div v-if="
                filtering === line.property &&
                prefill.filtered.length > 0"
                   class="focus-mask"
                   @click="filtering = null"></div>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-form>
    </v-card-text>
    <v-alert color="error" icon="warning" v-if="err" :value="err !== ''">
      {{ err }}
    </v-alert>
    <v-card-actions>
      <v-layout row justify-space-between>
        <v-btn
          outline
          color="primary"
          @click.stop="$emit('close')">
          Cancel
        </v-btn>
        <v-btn
          id="submit"
          @click="submit"
          color="primary"
          :disabled="!valid">
          {{ $lang.actions.save }}
        </v-btn>
      </v-layout>
    </v-card-actions>
  </v-card>
</template>

<script>

export default {
  props: ['address', 'postal', 'country'],
  data() {
    return {
      postalCodes: [],
      isLoading: false,
      formData: {
        country: '',
        postalCode: '',
        line1: '',
        line2: '',
        line3: '',
        line4: '',
        locality: '',
        townCity: '',
        latitude: '',
        longitude: '',
      },
      lastGoodPostal: '',
      validPostal: false,
      filtering: null,
      prefill: {
        original: [],
        filtered: [],
      },
      linesUK: [
        {
          property: 'postalCode',
          label: this.$lang.labels.postalCode,
          required: true,
        },
        {
          property: 'line1',
          label: this.$lang.labels.line1,
          required: true,
        },
        {
          property: 'line2',
          label: this.$lang.labels.line2,
          required: false,
        },
        {
          property: 'line3',
          label: this.$lang.labels.line3,
          required: false,
        },
        {
          property: 'line4',
          label: this.$lang.labels.line4,
          required: false,
        },
        {
          property: 'townCity',
          label: this.$lang.labels.townCity,
          required: true,
        },
        {
          property: 'locality',
          label: this.$lang.labels.locality,
          required: false,
        },
        {
          property: 'latitude',
          label: this.$lang.labels.latitude,
          required: true,
        },
        {
          property: 'longitude',
          label: this.$lang.labels.longitude,
          required: true,
        },
      ],
      linesAT: [
        {
          property: 'postalCode',
          label: this.$lang.labels.postalCode,
          required: true,
        },
        {
          property: 'line1',
          label: this.$lang.labels.line1AT,
          required: true,
        },
        {
          property: 'line2',
          label: this.$lang.labels.line2AT,
          required: true,
        },
        {
          property: 'line3',
          label: this.$lang.labels.line3AT,
          required: false,
        },
        {
          property: 'townCity',
          label: this.$lang.labels.townCity,
          required: true,
        },
        {
          property: 'latitude',
          label: this.$lang.labels.latitude,
          required: true,
        },
        {
          property: 'longitude',
          label: this.$lang.labels.longitude,
          required: true,
        },
      ],
      required: [
        v => !!v || this.$lang.errors.required,
      ],
      valid: false,
      err: '',
    };
  },
  computed: {
    filteredLines() {
      if (this.country === 'AT') {
        return this.linesAT.filter(line => ['postalCode', 'line1', 'line2', 'line3', 'townCity'].includes(line.property));
      }
      return this.linesUK;
    },
  },
  methods: {
    submit() {
      this.err = '';
      if (!this.validPostal) {
        this.err = this.$lang.errors.validPostalOrAddress;
        return;
      }
      let checkAddress = [];
      if (this.country === 'AT') {
        // eslint-disable-next-line max-len
        checkAddress = this.prefill.original.filter(address => this.formData.line1 === address.line1 && this.formData.line2 === address.line2 && this.formData.townCity === address.townCity && this.formData.latitude === address.latitude && this.formData.longitude === address.longitude);
      }
      if (this.country === 'UK') {
        // eslint-disable-next-line max-len
          checkAddress = this.prefill.original.filter(address => this.formData.line1 === address.line1 && this.formData.line2 === address.line2 && this.formData.townCity === address.townCity && this.formData.latitude === address.latitude && this.formData.longitude === address.longitude);
      }
      if (checkAddress.length === 0) {
        this.err = this.$lang.errors.validPostalOrAddress;
        return;
      }
      if (!this.formData.country) {
        this.formData.country = this.country;
      }
      this.$emit('submit', this.formData);
    },

    filterPrefill(value, target, clear = false) {
      if (this.prefill.original.length === 0) return;
      if (this.country === 'AT' && ['line3'].includes(target)) return;
      this.filtering = target;
      if (!value) {
        this.prefill.filtered = [];
        this.filtering = null;
        return;
      }
      // console.log('filter preffill 2', this.formData.postalCode);
      if (value === 'ALL_VALUES') {
        if (clear) {
          this.formData[target] = '';
        }
        this.prefill.filtered = this.prefill.original;
        return;
      }
      this.prefill.filtered = this.prefill.original.filter((address) => {
        // eslint-disable-next-line max-len
        if (address[target] && address[target].toLowerCase().includes(value.toLowerCase().trim())) return address;
        return false;
      });
      // console.log('filter preffill 4', this.formData.postalCode);
    },

    getPrefill(value, valueEvent, target) {
      if (target === 'postalCode') {
        this.getPostalCodes(valueEvent);
        return;
      }
      this.validPostal = false;
      this.prefill = {
        original: [],
        filtered: [],
      };
      this.err = '';
      if (this.lastGoodPostal !== '' && this.lastGoodPostal !== value) {
        this.lastGoodPostal = '';
        this.formData.line1 = '';
        if (this.country === 'AT') {
          this.formData.line2 = '';
          this.formData.line3 = '';
          this.formData.townCity = '';
        }
      }
      if (!value) return;
      if (this.country === 'UK' && (value.length < 5 || value.length > 16)) return;
      if (this.country === 'AT' && value.length !== 4) return;
      if ((!value || this.formData.line1.length < 3) && this.country === 'AT') return;
      // eslint-disable-next-line max-len
      const payload = {postalCode: value.trim().toUpperCase(), line1: this.formData.line1, country: this.country};
      this.formData.postalCode = value.trim().toUpperCase();
      this.isLoading = true;
      this.$http.post(`address/search`, payload)
        .then((res) => {
          if (res.body.status === 'Success') {
            this.validPostal = true;
            this.lastGoodPostal = value;
            this.prefill.original = res.body.addresses
              .map((current) => {
                const address = current;
                Object.keys(address).forEach((key) => {
                  address[key] = address[key].trim();
                });
                return address;
              });
            if (this.filterUniqueFields(this.prefill.original, 'townCity').length === 1) {
              this.formData.townCity = this.filterUniqueFields(this.prefill.original, 'townCity')[0];
            }
            this.isLoading = false;
            this.filterPrefill(valueEvent, target);
            return;
          }
          this.isLoading = false;
          this.validPostal = false;
        }).catch(err => {
        this.isLoading = false;
      });
    },

    getPostalCodes(code) {
      this.err = '';
      this.validPostal = false;
      if (!code) return;
      this.formData.postalCode = code.trim().toUpperCase();
      code = code.replace(/\s/g, '');
      if (this.country === 'UK' && (code.length < 4 || code.length > 16)) return;
      if (this.country === 'AT' && code.length !== 4) return;
      this.isLoading = true;
      this.$http.post(`address/validatepostalcode/${this.country}/${code}`)
        .then((res) => {
          if (res.body && res.status === 200) {
            this.isLoading = false;
            this.filtering = null;
            this.validPostal = true;
            this.postalCodes = [];
            setTimeout(() => {
              document.getElementById('1').focus();
            }, 50);
          }
        })
        .catch((err) => {
          this.err = err.body.status + ', ' + this.$lang.errors.postal;
          this.isLoading = false;
        });
    },

    setPostalCode(code) {
      this.validPostal = true;
      this.lastGoodPostal = code;
      this.formData.postalCode = code;
      this.formData.line1 = '';
      this.formData.line2 = '';
      this.formData.line3 = '';
      this.formData.latitude = '';
      this.formData.longitude = '';
      this.filtering = 'line1';
      this.getPrefill(this.formData.postalCode, '', 'line1');
      setTimeout(() => {
        document.getElementById('1').focus();
      }, 500);
    },

    replaceAddress(address, property) {
      this.err = '';
      if (this.country === 'UK') {
        this.linesUK.forEach((input) => {
          if (!address[input.property]) return;
          this.formData[input.property] = address[input.property];
        });
      } else if (this.country === 'AT') {
        this.formData[property] = address;
        switch (property) {
          case 'line1':
            this.formData.line2 = '';
            this.formData.line3 = '';
            this.formData.latitude = '';
            this.formData.longitude = '';
            setTimeout(() => {
              document.getElementById('2').focus();
            }, 50);
            break;
          // eslint-disable-next-line no-case-declarations
          case 'line2':
            this.formData.line3 = '';
            // eslint-disable-next-line max-len
            const chosenAddress = this.prefill.original.filter(x => x.line1 === this.formData.line1 && x.line2 === address);
            if (chosenAddress.length === 0) {
              this.filtering = null;
              this.err = this.$lang.errors.validPostalOrAddress;
              return;
            }
            this.formData.latitude = chosenAddress[0].latitude;
            this.formData.longitude = chosenAddress[0].longitude;
            break;
          case 'line3':
            // code block
            break;
          case 'townCity':
            // code block
            break;
          default:
          // code block
        }
      }
      this.filtering = null;
    },

    filterUniqueFields(addresses, property) {
      if (this.country === 'UK') return addresses;
      let filteredFieldList = addresses.map(address => address[property]);
      // eslint-disable-next-line max-len
      filteredFieldList = filteredFieldList.filter((item, index) => filteredFieldList.indexOf(item) === index);
      return filteredFieldList;
    },

    generateAddress(input) {
      let address = '';
      const tempKeys = Object.keys(input);
      tempKeys.forEach((key, index) => {
        if (['latitude', 'longitude', 'country'].includes(key)) address += '';
        else if (input[key]) address += tempKeys.length !== index - 1 ? `, ${input[key].trim()}` : `${input[key].trim()}`;
      });
      address = address.substring(2, address.length);
      return address;
    },
  },
  created() {
    if (typeof this.address !== 'undefined') {
      Object.keys(this.address).forEach((key) => {
        this.formData[key] = this.address[key];
      });
      this.getPrefill(this.formData.postalCode, 'ALL_VALUES', 'line1');
      return;
    }
    if (this.postal) {
      this.formData.postalCode = this.postal.toUpperCase();
      this.getPrefill(this.formData.postalCode, 'ALL_VALUES', 'line1');
      setTimeout(() => {
        document.getElementById('1').focus();
      }, 50);
    } else {
      setTimeout(() => {
        document.getElementById('0').focus();
      }, 200);
    }
  },
};
</script>

<style lang="scss" scoped>
.d-none {
  display: none;
}

.prefill {
  position: relative;

  input {
    position: relative;
    z-index: 100;
  }

  ul {
    position: absolute;
    top: calc(100% - 26px);
    left: 0;
    list-style: none;
    padding: 0;
    width: 100%;
    max-height: 350px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.2);
    z-index: 100;

    li {
      position: relative;
      padding: 0.5rem;
      cursor: pointer;
      transition: background 125ms ease-in;

      &:not(:last-child) {
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      }

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }

  .focus-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99;
  }
}

@media only screen and (max-width: 768px) {
  .card__text {
    overflow-y: scroll;
  }
}
</style>



// WEBPACK FOOTER //
// AddressModal.vue?b9bc45e8